/* eslint-disable import/named */
import { Preview } from "@creatomate/preview";
import { CreatomateAnimations, ElementSource, Story, Template } from "chq";
import { getImageFromStory } from "app/utils";
import imagesSizes from "constants/imagesSizes";
import { flattenMultiImageStories } from "components/stories/video-studio/utils";
import { ELEMENT_NAMES, IMAGE_SLIDE_DURATION, IMAGE_TYPE, VIDEO_TYPE } from "components/stories/video-studio/utils/constants";
import createElement from "components/stories/video-studio/utils/creatomate/createElement";
import getText from "components/editor/getText";
import { ImplementorMontageTemplateParams } from "components/stories/video-studio/utils/creatomate/setMontageCustomTemplate";
import findElementByName from "../../utils/findElementByName";

export const createImageSlideShowWithCaptionsStory = async (
  preview: Preview,
  template: Template,
  story: Story,
  storyDuration: number,
  mediaWidth: number,
  index: number,
  time?: number
): Promise<string | undefined> => {
  const mediaObject = template.animationsPerIndex as unknown as CreatomateAnimations;
  const mediaLinearAnimation = mediaObject.media[index % 3];
  const textAnimations = mediaObject.text[index % 3];

  const { mediaType, url } = story.media;
  const { duration } = preview.state || { duration: 1 };

  const newVideoId = await createElement({
    type: mediaType,
    source: mediaType === VIDEO_TYPE ? url : getImageFromStory(imagesSizes.FULL, story),
    time: time ?? (duration - 1),
    fit: "cover",
    duration: storyDuration,
    track: 1,
    width: `${mediaWidth}%`,
    animations: [template.commonAnimation, mediaLinearAnimation],
    ...(mediaType === "image" && { name: `${story.creator.name}'s image` })
  }, preview);

  // create text
  await createElement({
    type: "text",
    text: getText(story.body),
    time: time ?? (duration - 1),
    duration: storyDuration - 1,
    ...template.commonTextProperties,
    ...template.textProperties?.[index % 3],
    animations: textAnimations
  }, preview);

  return newVideoId;
};

const imageSlideshowWithCaptionsTemplate = async ({
  template,
  preview,
  selectedStories,
  onStoryUpdate
}: ImplementorMontageTemplateParams) => {
  const { state } = preview;
  const { width: previewWidth = 1280, height: previewHeight = 720, elements } = state || {};

  const json = preview.getSource();
  const elementsJson = json?.elements;

  const storyElements = [
    findElementByName(elementsJson, ELEMENT_NAMES.STORY1),
    findElementByName(elementsJson, ELEMENT_NAMES.STORY2),
    findElementByName(elementsJson, ELEMENT_NAMES.STORY3)
  ];
  const textElements = [
    findElementByName(elementsJson, ELEMENT_NAMES.CAPTION1),
    findElementByName(elementsJson, ELEMENT_NAMES.CAPTION2),
    findElementByName(elementsJson, ELEMENT_NAMES.CAPTION3)
  ];

  const deleteStoryByIndex = async (index: number): Promise<void> => {
    const json = preview.getSource();
    const { elements } = json;
    let newElements = [...elements];
    newElements = newElements.filter((element: ElementSource) => (
      element.id !== textElements[index]?.id
      && element.id !== textElements[index]?.id
      && element.id !== storyElements[index]?.id
    ));
    return preview.setSource({ ...json, elements: newElements });
  };

  const flattenStories = flattenMultiImageStories(selectedStories);
  let modifications = {};
  let time = 0;

  const getStoryModifications = (story: Story, index: number, time: number) => {
    const { media } = story;
    const { mediaType, url, width = 1, height = 1, duration = 0 } = media;
    const storyDuration = mediaType === IMAGE_TYPE ? IMAGE_SLIDE_DURATION : duration;
    const heightProportion = height / previewHeight;
    const mediaWidth = (100 * (width / heightProportion)) / previewWidth;

    let storyModifications: { [key: string]: any } = {
      [`${storyElements[index]?.id}.source`]: mediaType === VIDEO_TYPE ? url : getImageFromStory(imagesSizes.FULL, story),
      [`${storyElements[index]?.id}.type`]: mediaType,
      [`${storyElements[index]?.id}.width`]: `${mediaWidth}%`,
      [`${storyElements[index]?.id}.duration`]: storyDuration,
      [`${storyElements[index]?.id}.time`]: time,
      [`${textElements[index]?.id}.text`]: getText(story.body),
      [`${textElements[index]?.id}.time`]: time,
      [`${textElements[index]?.id}.duration`]: storyDuration - 1
    };

    if (mediaType === IMAGE_TYPE) {
      storyModifications = {
        ...storyModifications,
        [`${storyElements[index]?.id}.name`]: `${story.creator.name}'s image`
      };
    }

    return { mediaWidth, storyModifications, storyDuration };
  };

  for (let index = 0; index < flattenStories.length; index += 1) {
    const story = flattenStories[index];
    const { mediaWidth, storyModifications, storyDuration } = getStoryModifications(
      story, index, time
    );

    if (index < 3) {
      modifications = { ...modifications, ...storyModifications };
      onStoryUpdate({ ...story, creatomateId: storyElements[index]?.id });
    } else if (template.animationsPerIndex && !Array.isArray(template.animationsPerIndex)) {
      // eslint-disable-next-line no-await-in-loop
      const elementId = await createImageSlideShowWithCaptionsStory(
        preview, template, story, storyDuration, mediaWidth, index, time
      );
      onStoryUpdate({ ...story, creatomateId: elementId });
    }
    // story duration - animation (1 second per transition)
    time += storyDuration - 1;
  }

  if (flattenStories.length < 3) {
    for (let index = flattenStories.length; index < 3; index += 1) {
      // eslint-disable-next-line no-await-in-loop
      await deleteStoryByIndex(index);
    }
  }

  modifications = {
    ...modifications,
    duration: time
  };
  await preview.applyModifications(modifications);
};

export default imageSlideshowWithCaptionsTemplate;
